import { Add } from "@mui/icons-material";
import { Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { useMedicalCareContext } from "../../../../contexts/MedicalCareContext";
import { useWatch } from "react-hook-form";
import { FormAutoComplete } from "../../../../components/form";
import { GetCIAP2 } from "@/services/terminology/GetCIAP2";
import { toast } from "react-toastify";

interface ProblemOption {
  display?: string;
  value?: string;
  code?: string;
}

export function AddProblema() {
  const { control, unregister, problems, setProblems, setValue } =
    useMedicalCareContext();
  const [currentIndex, setCurrentIndex] = useState<number>(
    problems?.length || 0
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [searchProblems, setSearchProblems] = useState<string>("");
  const [problemsOptions, setProblemsOptions] = useState<any>();
  // Referência para armazenar o timeout atual
  const timeoutRef = useRef<any>(null);
  // Estado para armazenar o valor atual sendo digitado (igual ao componente Condutas)
  const [currentProblema, setCurrentProblema] = useState<string>("");
  const formValues = useWatch({
    name: "New_Problema",
    control,
  }) as ProblemOption[];

  // Mapeia as opções de CIAP2
  const problemsList =
    problemsOptions?.expansion?.contains?.map((el: any) => ({
      ...el,
      label: `${el?.display} - ${el?.code}`,
      value: el?.code,
    })) || [];

  // Função para verificar duplicatas - abordagem mais direta
  const checkDuplicatesWithDelay = (value: string) => {
    // Limpa qualquer timeout existente
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    if (!value || value.trim() === '') return;
    
    // Verifica se o problema já existe
    const existsInCurrentList = problems.some(
      (problem: string) => problem.trim() === value.trim()
    );
    
    const flatOpenItens = document.querySelectorAll('.lista-problemas-abertos .MuiTypography-root');
    const existsInOpenList = Array.from(flatOpenItens).some(
      (item: Element) => item.textContent?.trim() === value.trim()
    );
    
    if (existsInCurrentList || existsInOpenList) {
      
      // Timeout para mostrar o aviso e depois limpar
      timeoutRef.current = setTimeout(() => {
        // Mostrar toast
        toast.warning(`"${value}" já está na lista de problemas`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        
        // ABORDAGEM SUPER DIRETA: Usar o DOM para limpar o campo
        const inputs = document.querySelectorAll('.MuiAutocomplete-input');
        inputs.forEach(input => {
          (input as HTMLInputElement).value = '';
        });
        
        // Limpar no React Form
        setValue(`New_Problema.${currentIndex}`, undefined);
        setValue(`New_Problema.${currentIndex}`, null);
        setValue(`New_Problema.${currentIndex}.display`, null);
        setValue(`New_Problema.${currentIndex}.code`, null);
        setValue(`New_Problema.${currentIndex}.value`, null);
        unregister(`New_Problema.${currentIndex}`);
        
        // Limpar também o freeSolo
        setValue(`freeSoloProblem`, undefined);
        
        // Limpar estado local - usar setState diretamente
        setSearchProblems("");
        setCurrentProblema("");
        
        // Fazer uma atualização forçada do componente após um curto delay
        setTimeout(() => {
          // Forçar atualização do DOM novamente
          inputs.forEach(input => {
            (input as HTMLInputElement).value = '';
            
            // Forçar eventos de change e input
            const changeEvent = new Event('change', { bubbles: true });
            input.dispatchEvent(changeEvent);
            
            const inputEvent = new Event('input', { bubbles: true });
            input.dispatchEvent(inputEvent);
          });
        }, 10);
      }, 2000); // 2 segundos de delay conforme solicitado
    }
  };

  // Efeito específico para monitorar mudanças no valor atual do problema
  useEffect(() => {
    if (currentProblema) {
      checkDuplicatesWithDelay(currentProblema);
    }
  }, [currentProblema]);
  
  // Flag para controlar se o searchProblems deve ser processado
  const shouldProcessSearchRef = useRef(true);
  
  useEffect(() => {
    // Se não devemos processar (porque acabamos de limpar), ignore este ciclo
    if (!shouldProcessSearchRef.current) {
      shouldProcessSearchRef.current = true;
      return;
    }
    
    async function fetchProblems() {
      const valueSetResponse = await GetCIAP2({
        filter: searchProblems,
      });
      setProblemsOptions(valueSetResponse?.data);
      setLoading(false);
    }

    const debounce = setTimeout(() => {
      if (searchProblems) {
        setLoading(true);
        fetchProblems();
      }
    }, 500);

    return () => clearTimeout(debounce);
  }, [searchProblems]);

  const handleAddProblema = () => {
    const currentValue = formValues?.[currentIndex];
    if (currentValue?.display || currentValue?.value || searchProblems) {
      const problemValue =
      currentValue?.display || currentValue?.value || searchProblems;
      
      if (problemValue?.trim()) {
        // Verifica se o problema já existe na lista de problemas
        const existsInCurrentList = problems.some(
          (problem: string) => problem === problemValue
        );
        
        // Verifica se já existe nos problemas abertos (flatOpenItens do Problemas.tsx)
        const flatOpenItens = document.querySelectorAll('.lista-problemas-abertos .MuiTypography-root');
        const existsInOpenList = Array.from(flatOpenItens).some(
          (item: Element) => item.textContent?.trim() === problemValue.trim()
        );
        
        if (existsInCurrentList || existsInOpenList) {
          // Se já existe, mostrar um toast de aviso
          toast.warning(`"${problemValue}" já está na lista de problemas`, {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          
          // Limpar campos - abordagem mais direta e forçada
          setValue(`New_Problema.${currentIndex}`, null);
          setValue(`freeSoloProblem`, undefined);
          setSearchProblems("");
          
          // Forçar o React a redesenhar o componente
          setTimeout(() => {
            const formElement = document.querySelector('form');
            if (formElement) {
              const inputs = formElement.querySelectorAll('input[type="text"]');
              inputs.forEach((input: any) => {
                if (input.value.includes(problemValue)) {
                  input.value = '';
                }
              });
            }
            
            // Limpeza adicional de valores do formulário
            setValue(`New_Problema.${currentIndex}`, undefined);
            unregister(`New_Problema.${currentIndex}`);
          }, 50);
          
        } else {
          // Se não existe, adicionar normalmente
          setProblems((prev: string[]) => [...prev, problemValue]);
          setValue(`New_Problema.${currentIndex}`, null);
          setCurrentIndex((prev: number) => prev + 1);
          setSearchProblems("");
        }
      }
    }
    //Set value abaixo corrige duplicação de problemas
    setValue(`freeSoloProblem`, undefined);
  };

  return (
    <Stack spacing={2}>
      {problems?.length > 0 && (
        <Stack
          direction="row"
          flexWrap="wrap"
          spacing={1}
          paddingBottom={1}
          gap={1}
        >
          {problems.map(
            (problema: string, index: number) =>
              problema?.length > 0 && (
                <Chip
                  key={index}
                  sx={{ padding: "8px", height: "auto" }}
                  label={
                    problema.length > 85 ? (
                      <Tooltip
                        placement="right"
                        title={problema}
                        slotProps={{
                          popper: {
                            modifiers: [
                              {
                                name: "offset",
                                options: { offset: [80, 20] },
                              },
                            ],
                          },
                        }}
                      >
                        <Typography>{problema.substring(0, 85)}...</Typography>
                      </Tooltip>
                    ) : (
                      <Typography>{problema}</Typography>
                    )
                  }
                  onDelete={() => {
                    setProblems((prev: string[]) => {
                      const updated = [...prev];
                      updated[index] = "";
                      return updated;
                    });
                    unregister(`New_Problema.${index}`);
                  }}
                />
              )
          )}
        </Stack>
      )}

      <Stack spacing={1}>
        <FormAutoComplete
          key={`problema-input-${currentIndex}`}
          freeSolo
          loading={loading}
          name={`New_Problema.${currentIndex}`}
          label="Problema"
          control={control}
          onInputChange={(e: any) => {
            if (e?.target?.value?.length) {
              const value = e?.target?.value;
              setSearchProblems(value);
              setValue(`freeSoloProblem`, value);
              // Importante: atualizar também o currentProblema para a verificação de duplicatas
              setCurrentProblema(value);
            } else {
              setSearchProblems("");
              setCurrentProblema("");
            }
          }}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddProblema();
              //Set value abaixo corrige duplicação de problemas
              setTimeout(() => setValue("freeSoloProblem", undefined), 0);
            }
          }}
          selectOptions={problemsList}
          autoFocus
        />

        <Stack direction="row" justifyContent="flex-end">
          <Button
            sx={{ width: "fit-content" }}
            onClick={handleAddProblema}
            variant="text"
          >
            <Add />
            Adicionar
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}
